/**
 * チュートリアル画面のUI配線ヘルパー — 純粋JS（DOM / three.js 非依存、
 * node でテスト可能）。
 *
 * 役割は2つだけ:
 *   1. 盤入力（マスタップ / 溝タップ / 壁ピック）→ Move 化。
 *      通常対局の main.js が組み立てる Move と完全に同じ形にする。
 *   2. その Move をチュートリアルエンジンの tryMove へ渡し、UI が実行すべき
 *      副作用（SE 名・トースト文言）まで含めた結果に変換する。
 *
 * ルール・レッスン進行のロジックは一切持たない（core / tutorial の責務）。
 */

import { applyMove } from '../core/index.js';
import { seForMove } from './sfx.js';

/**
 * マスタップ → Move。
 * ability（{id} | null）が与えられたとき（能力モード中）は能力手、
 * それ以外は通常の駒移動。main.js の pointerup と同じ組み立て。
 */
export function moveForCellTap(cell, ability = null) {
  const to = { col: cell.col, row: cell.row };
  if (ability) return { type: 'ability', ability: ability.id, to };
  return { type: 'move', to };
}

/** 溝スロット（{at:{col,row}, orientation}）→ 壁設置 Move。 */
export function moveForWallSlot(slot) {
  return {
    type: 'wall',
    at: { col: slot.at.col, row: slot.at.row },
    orientation: slot.orientation,
  };
}

/** 設置済み壁ピック（{col,row,orientation}）→ 壁除去の能力 Move。 */
export function moveForWallPick(wall) {
  return {
    type: 'ability',
    ability: 'removeWall',
    at: { col: wall.col, row: wall.row },
    orientation: wall.orientation,
  };
}

/**
 * Move をチュートリアルエンジンへ渡し、UI が実行すべきことを返す。
 *
 *   advance      → { result:'advance', message, done, se }
 *                  se は正解手そのものの効果音（seForMove を流用。壁= wall、
 *                  ジャンプ= jump 等）。message はあれば成功トースト。
 *   wrong/blocked→ { result, message, se:'select' }
 *                  illegal SE は使わず、優しい選択音 + message トースト。
 *
 * エンジンは wrong / blocked で状態を進めないので、この関数も純粋
 * （advance 時のみエンジン内部の状態が進む）。
 */
export function submitToTutorial(tut, move) {
  const prev = tut.state;
  const res = tut.tryMove(move);
  if (res.result === 'advance') {
    // エンジンはスクリプトの相手手まで進めてしまうため、SE 判定用に
    // 「プレイヤーの手だけ適用した局面」を core の applyMove で再現する。
    return { ...res, se: seForMove(prev, applyMove(prev, move)) };
  }
  return { ...res, se: 'select' };
}

/**
 * 盤描画をアニメーションなしで即時配置（snap）すべきか。
 * レッスン/ステップの setup で局面が作り直されたとき（= 新しい state の
 * history が直前の state の続きでないとき）は snap、正解手 + スクリプト
 * 相手手（最大2手）の続きなら通常のアニメーション。
 */
export function shouldSnapBoard(prevState, nextState) {
  if (!prevState || !nextState) return true;
  const ph = prevState.history;
  const nh = nextState.history;
  if (nh.length < ph.length || nh.length > ph.length + 2) return true;
  return !ph.every((m, i) => JSON.stringify(m) === JSON.stringify(nh[i]));
}
